import { usePoolData } from '@/hooks/usePoolData';
import { useIncentiveStakingData } from '@/hooks/useIncentiveStakingData';
import { LoadingSkeleton } from '@/components/LoadingSkeleton';
import {
  formatCurrency,
  getIncentiveKey,
} from '@/utils/common';
import {
  Card,
  CardHeader,
  CardContent,
  CardFooter,
  ChartIcon,
} from '@/components/ui';
import { formatEther, keccak256 } from 'ethers';
import { useIncentiveAPR } from '@/hooks/useIncentiveApr';

interface IncentiveKey {
  id?: string;
  rewardToken: string;
  pool: string;
  startTime: bigint;
  endTime: bigint;
  refundee: string | undefined;
  reward: bigint;
}

interface DataDashboardProps {
  selectedIncentive?: IncentiveKey;
  showStakingInfo: boolean;
}

export default function DataDashboard({
  selectedIncentive,
  showStakingInfo,
}: DataDashboardProps) {
  const poolData = usePoolData();
  const {
    totalStakedValue,
    userStakedValue,
    isLoading: isLoadingStaking,
  } = useIncentiveStakingData(keccak256(getIncentiveKey(selectedIncentive)));

  const apr = useIncentiveAPR(keccak256(getIncentiveKey(selectedIncentive)));
  const isLive = selectedIncentive
    ? Math.floor(Date.now() / 1000) <= Number(selectedIncentive.endTime)
    : false;

  return (
    <Card glow>
      <CardHeader
        icon={<ChartIcon className="w-5 h-5 text-white" />}
        title="Live Protocol Data"
      />

      <CardContent>
        <div className="flex flex-col gap-4">
          {/* Pool Information */}
          <div>
            <h4 className="text-sm font-semibold text-slate-600 mb-2">
              Pool Information
            </h4>
            <div className="space-y-1">
              <div className="flex justify-between items-center py-1.5 border-b border-slate-100">
                <span className="text-slate-600 text-sm">Pool:</span>
                <span className="text-slate-900 font-mono text-xs">
                  {poolData.isLoading ? (
                    <LoadingSkeleton className="w-32 h-4" />
                  ) : poolData.poolAddress ? (
                    `${poolData.poolAddress.slice(0, 6)}...${poolData.poolAddress.slice(-4)}`
                  ) : (
                    'Not found'
                  )}
                </span>
              </div>
              <div className="flex justify-between items-center py-1.5 border-b border-slate-100">
                <span className="text-slate-600 text-sm">TVL:</span>
                <span className="text-slate-900 font-semibold text-sm">
                  {poolData.isLoading ? (
                    <LoadingSkeleton className="w-16 h-4" />
                  ) : (
                    formatCurrency(poolData.tvl)
                  )}
                </span>
              </div>
              <div className="flex justify-between items-center py-1.5 border-b border-slate-100">
                <span className="text-slate-600 text-sm">JOCX Balance:</span>
                <span className="text-slate-900 font-semibold text-sm">
                  {poolData.isLoading ? (
                    <LoadingSkeleton className="w-16 h-4" />
                  ) : (
                    formatCurrency(poolData.jocxBalance, '')
                  )}
                </span>
              </div>
              <div className="flex justify-between items-center py-1.5 border-b border-slate-100">
                <span className="text-slate-600 text-sm">USDT Balance:</span>
                <span className="text-slate-900 font-semibold text-sm">
                  {poolData.isLoading ? (
                    <LoadingSkeleton className="w-16 h-4" />
                  ) : (
                    formatCurrency(poolData.usdtBalance)
                  )}
                </span>
              </div>
              <div className="flex justify-between items-center py-1.5">
                <span className="text-slate-600 text-sm">JOCX Price:</span>
                <span className="text-slate-900 font-semibold text-sm">
                  {poolData.isLoading ? (
                    <LoadingSkeleton className="w-16 h-4" />
                  ) : (
                    `${poolData.jocxPrice.toFixed(6)} $`
                  )}
                </span>
              </div>
            </div>
          </div>

          {/* Staking Information */}
          {showStakingInfo && (
            <div className="pt-2 border-t border-slate-200">
              <h4 className="text-sm font-semibold text-slate-600 mb-2">
                Staking Information
              </h4>
              <div className="space-y-1">
                <div className="flex justify-between items-center py-1.5 border-b border-slate-100">
                  <span className="text-slate-600 text-sm">Reward:</span>
                  <span className="text-slate-900 font-semibold text-sm">
                    {!selectedIncentive ? (
                      <LoadingSkeleton className="w-16 h-4" />
                    ) : (
                      formatEther(selectedIncentive.reward) + ' JOCX'
                    )}
                  </span>
                </div>
                <div className="flex justify-between items-center py-1.5 border-b border-slate-100">
                  <span className="text-slate-600 text-sm">Total Staked:</span>
                  <span className="text-slate-900 font-semibold text-sm">
                    {!selectedIncentive || isLoadingStaking ? (
                      <LoadingSkeleton className="w-16 h-4" />
                    ) : (
                      '$' + totalStakedValue.toFixed(2)
                    )}
                  </span>
                </div>
                <div className="flex justify-between items-center py-1.5 border-b border-slate-100">
                  <span className="text-slate-600 text-sm">You Staked:</span>
                  <span className="text-slate-900 font-semibold text-sm">
                    {!selectedIncentive || isLoadingStaking ? (
                      <LoadingSkeleton className="w-16 h-4" />
                    ) : (
                      '$' + userStakedValue.toFixed(2)
                    )}
                  </span>
                </div>
                <div className="flex justify-between items-center py-1.5">
                  <span className="text-slate-600 text-sm">APR:</span>
                  <span className="text-slate-900 font-semibold text-sm">
                    {!selectedIncentive || isLoadingStaking ? (
                      <LoadingSkeleton className="w-16 h-4" />
                    ) : isLive ? (
                      apr + '%'
                    ) : (
                      <span className="text-slate-400">Ended</span>
                    )}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
      </CardContent>

      <CardFooter>
        <div className="flex items-center justify-between text-sm text-slate-500 w-full">
          <span>Last updated:</span>
          <span>
            {poolData.lastUpdated
              ? poolData.lastUpdated.toLocaleTimeString()
              : 'Loading...'}
          </span>
        </div>
      </CardFooter>
    </Card>
  );
}
